import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Loader2, CheckCircle2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <header className="border-b border-border">
      <div className="max-w-2xl mx-auto flex items-center gap-2 px-6 h-16">
        <div className="w-8 h-8 bg-[#007AFF] flex items-center justify-center">
          <Radio className="w-5 h-5 text-white" />
        </div>
        <span className="font-heading font-black text-lg tracking-tighter">Incident Tracking &amp; Inquiry Services</span>
      </div>
    </header>
    <main className="flex-1 max-w-2xl w-full mx-auto px-6 py-12">{children}</main>
    <footer className="border-t border-border py-6 text-center">
      <p className="text-xs text-muted-foreground font-mono">Every story. Both sides. On the record.</p>
    </footer>
  </div>
);

export default function RespondInquiry() {
  const { token } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [state, setState] = useState("loading"); // loading | form | already | notfound | done
  const [name, setName] = useState("");
  const [statement, setStatement] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/inquiries/public/${token}`)
      .then(({ data }) => {
        setInquiry(data);
        setState(data.responded ? "already" : "form");
      })
      .catch(() => setState("notfound"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/inquiries/public/${token}/respond`, { responder_name: name, statement });
      setState("done");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 409) setState("already");
      else toast.error(detail || "Could not submit your statement. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading") {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-muted-foreground animate-pulse" data-testid="respond-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> <span className="font-mono text-sm">Loading inquiry…</span>
        </div>
      </Shell>
    );
  }

  if (state === "notfound") {
    return (
      <Shell>
        <div data-testid="respond-notfound">
          <h1 className="font-heading text-2xl font-black tracking-tighter mb-3">This inquiry link isn't valid</h1>
          <p className="text-sm text-muted-foreground">It may have been removed or the link was copied incompletely. Please check the link in your email.</p>
        </div>
      </Shell>
    );
  }

  if (state === "already" || state === "done") {
    return (
      <Shell>
        <div className="text-center py-10" data-testid={state === "done" ? "respond-done" : "respond-already"}>
          <CheckCircle2 className="w-10 h-10 text-[#34C759] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-black tracking-tighter mb-3">
            {state === "done" ? "Your statement is on the record" : "A statement is already on the record"}
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {state === "done"
              ? "Thank you. Your account of events has been recorded verbatim and delivered. No further action is needed."
              : "A response to this inquiry has already been submitted and recorded. No further action is needed."}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="respond-form-page">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#007AFF] mb-3">Statement Request</p>
        <h1 className="font-heading text-3xl font-black tracking-tighter mb-4">
          {inquiry.business_name} — what happened?
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed mb-2">
          <span className="text-white font-medium">{inquiry.from_name}</span> has asked, through our neutral
          inquiry service, for your account of the following:
        </p>
        <div className="border-l-2 border-[#007AFF] bg-[#0A0A0A] px-4 py-3 mb-6">
          <p className="text-sm leading-relaxed" data-testid="respond-message">{inquiry.message}</p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-8">
          This is not a complaint or a legal demand — it's your chance to put your side of the story on the
          record, in your own words. Your statement is recorded verbatim, never paraphrased, and shared only
          with the party who asked.
        </p>

        <form onSubmit={submit}>
          <div className="mb-4">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Your name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="respond-name"
              placeholder="Jane Alvarez, Operations Manager"
              className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" />
          </div>
          <div className="mb-6">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Your statement — what happened?</Label>
            <Textarea required minLength={10} value={statement} onChange={(e) => setStatement(e.target.value)}
              data-testid="respond-statement" rows={8}
              placeholder="In your own words, describe what happened from your side…"
              className="mt-2 bg-[#0A0A0A] border-border rounded-sm resize-y leading-relaxed focus-visible:ring-[#007AFF]" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="respond-submit-btn"
            className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2 h-11">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? "Submitting…" : "Put it on the record"}
          </Button>
        </form>
      </div>
    </Shell>
  );
}
